import { useState } from "react";
import { toast } from "sonner";
import { Edit, Trash2, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useFetchSaccoRebates,
  useDeleteSaccoRebate,
} from "@saccoHooks";
import { RebateForm } from "./RebateForm";
import {SaccoRebate} from "@/@types/sacco.models.ts";

interface RebateListProps {
  membershipId: string;
  nativeCurrency?: string;
  displayCurrency?: string;
  convert?: (amount: number, from: string, to: string) => number;
  isLoading?: boolean;
  error?: Error | null;
}

function formatCurrency(value: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

export function RebateList({
  membershipId,
  nativeCurrency = 'KES',
  displayCurrency = 'KES',
  convert,
  isLoading: ratesLoading = false,
  error: ratesError = null,
}: RebateListProps) {
  const { data: rebates = [], isLoading } = useFetchSaccoRebates(membershipId);
  const deleteRebate = useDeleteSaccoRebate(membershipId);

  const [formOpen, setFormOpen] = useState(false);
  const [editingRebate, setEditingRebate] = useState<SaccoRebate | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Group by year descending
  const byYear = rebates.reduce<Record<number, SaccoRebate[]>>((acc, r) => {
    (acc[r.year] ??= []).push(r);
    return acc;
  }, {});
  const years = Object.keys(byYear)
    .map(Number)
    .sort((a, b) => b - a);

  const totalRebates = rebates.reduce((s, r) => s + Number(r.rebate_amount), 0);
  const totalDividends = rebates.reduce((s, r) => s + Number(r.dividend_amount), 0);

  const effectiveCurrency = ratesError ? nativeCurrency : displayCurrency;

  function convertAmt(raw: number): number {
    if (ratesError || ratesLoading || !convert || displayCurrency === nativeCurrency) return raw;
    try {
      return convert(raw, nativeCurrency, displayCurrency);
    } catch {
      return raw;
    }
  }

  function handleEdit(rebate: SaccoRebate) {
    setEditingRebate(rebate);
    setFormOpen(true);
  }

  function handleAddNew() {
    setEditingRebate(undefined);
    setFormOpen(true);
  }

  function handleDeleteClick(id: string) {
    setDeletingId(id);
    setDeleteDialogOpen(true);
  }

  async function handleConfirmDelete() {
    if (!deletingId) return;
    try {
      await deleteRebate.mutateAsync(deletingId);
      toast.success("Rebate deleted successfully");
    } catch {
      toast.error("Failed to delete rebate");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={handleAddNew} size="sm">
          <PlusCircle className="h-4 w-4 mr-2" />
          Add Rebate
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground text-center py-4">Loading rebates...</p>
      ) : rebates.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          No rebates recorded yet. Add your first rebate above.
        </p>
      ) : (
        <div className="space-y-6">
          {years.map((year) => (
            <div key={year}>
              <h3 className="text-sm font-semibold text-muted-foreground mb-2">{year}</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Year</TableHead>
                    <TableHead className="text-right">Rebate</TableHead>
                    <TableHead className="text-right">Dividend</TableHead>
                    <TableHead>Note</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {byYear[year].map((rebate) => (
                    <TableRow key={rebate.id}>
                      <TableCell>{rebate.year}</TableCell>
                      <TableCell className="text-right font-medium">
                        {ratesLoading ? (
                          <Skeleton className="h-4 w-20 inline-block" />
                        ) : (
                          formatCurrency(convertAmt(Number(rebate.rebate_amount)), effectiveCurrency)
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {ratesLoading ? (
                          <Skeleton className="h-4 w-20 inline-block" />
                        ) : (
                          formatCurrency(convertAmt(Number(rebate.dividend_amount)), effectiveCurrency)
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {rebate.note ?? "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(rebate)}
                            aria-label="Edit rebate"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteClick(rebate.id)}
                            disabled={deleteRebate.isPending}
                            aria-label="Delete rebate"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))}

          <div className="border-t pt-4 flex justify-end gap-8 text-sm font-medium">
            <span>
              Total Rebates: <span className="text-foreground">{formatCurrency(convertAmt(totalRebates), effectiveCurrency)}</span>
            </span>
            <span>
              Total Dividends: <span className="text-foreground">{formatCurrency(convertAmt(totalDividends), effectiveCurrency)}</span>
            </span>
          </div>
        </div>
      )}

      <RebateForm
        membershipId={membershipId}
        nativeCurrency={nativeCurrency}
        rebate={editingRebate}
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditingRebate(undefined);
        }}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Rebate</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this rebate? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
